import {
  INTERACTIVE_TEMPLATE_FIELD_NAME_REGEX,
  INTERACTIVE_TEMPLATE_OPTIONS_MAX,
  type InteractiveTemplateDefinition,
  TEMPLATE_INPUT_TYPES,
} from './interactive-template';

/**
 * The built-in templates (wayai-pro/wayai#4751 decision 1): `choices`, `buttons` and `form`, which
 * every hub has with no builder config. They are ordinary definitions, drawn by the same renderers and
 * checked by the same validator; their names are reserved (`INTERACTIVE_BUILTIN_TEMPLATE_NAMES`).
 *
 * A message pins a built-in by name and `version` like any template (decision 8). Changing one adds a
 * new entry with the next version and keeps the old: past messages render and resolve their actions
 * against the version they were sent with.
 *
 * They are written in their stored form (`InteractiveTemplateDefinition`, every default filled), so
 * loading this module parses nothing; the tests hold each one to `validateInteractiveTemplateDefinition`.
 */

const OPTION_ITEM = {
  type: 'object',
  properties: {
    id: { type: 'string', description: 'What the agent receives when this one is picked' },
    label: { type: 'string', description: 'What the person sees' },
  },
  required: ['id', 'label'],
};

const choices: InteractiveTemplateDefinition = {
  name: 'choices',
  description: 'A question with up to 10 options, each with an optional description. The person picks one; the agent receives its id and label.',
  params: {
    type: 'object',
    properties: {
      prompt: { type: 'string', description: 'The question, shown above the options' },
      options: {
        type: 'array',
        minItems: 1,
        maxItems: 10,
        items: {
          ...OPTION_ITEM,
          properties: { ...OPTION_ITEM.properties, description: { type: 'string', description: 'A line under the label' } },
        },
      },
    },
    required: ['options'],
  },
  views: {
    main: {
      type: 'card',
      children: [
        { type: 'text', if: 'params.prompt', text: '{{params.prompt}}' },
        {
          type: 'list',
          each: 'params.options',
          item: {
            type: 'col',
            gap: 1,
            children: [
              { type: 'button', label: '{{item.label}}', action: 'choose' },
              { type: 'text', if: 'item.description', text: '{{item.description}}', size: 'sm', color: 'secondary' },
            ],
          },
        },
      ],
    },
  },
  actions: {
    choose: { type: 'send', text: '{{item.label}}', values: { id: '{{item.id}}' } },
  },
  fallback:
    '{{#if params.prompt}}{{params.prompt}}\n{{/if}}{{#each params.options}}{{@n}}. {{label}}{{#if description}} — {{description}}{{/if}}\n{{/each}}',
};

const buttons: InteractiveTemplateDefinition = {
  name: 'buttons',
  description: 'A message with up to 3 buttons under it. The person presses one; the agent receives its id and label.',
  params: {
    type: 'object',
    properties: {
      text: { type: 'string', description: 'The message above the buttons' },
      buttons: { type: 'array', minItems: 1, maxItems: 3, items: OPTION_ITEM },
    },
    required: ['text', 'buttons'],
  },
  views: {
    main: {
      type: 'card',
      children: [
        { type: 'text', text: '{{params.text}}' },
        {
          type: 'list',
          each: 'params.buttons',
          direction: 'horizontal',
          item: { type: 'button', label: '{{item.label}}', action: 'press' },
        },
      ],
    },
  },
  actions: {
    press: { type: 'send', text: '{{item.label}}', values: { id: '{{item.id}}' } },
  },
  fallback: '{{params.text}}\n{{#each params.buttons}}{{@n}}. {{label}}\n{{/each}}',
};

const form: InteractiveTemplateDefinition = {
  name: 'form',
  description:
    'A form of up to 20 fields, each a text input, a select or a checkbox, optionally pre-filled and annotated with where its value came from. The person submits it; the agent receives the values.',
  params: {
    type: 'object',
    properties: {
      title: { type: 'string', description: 'Shown above the fields' },
      submit_label: { type: 'string', description: 'The submit button\'s label' },
      fields: {
        type: 'array',
        minItems: 1,
        maxItems: 20,
        items: {
          type: 'object',
          properties: {
            name: { type: 'string', pattern: INTERACTIVE_TEMPLATE_FIELD_NAME_REGEX.source, description: 'The key the value is submitted under' },
            label: { type: 'string' },
            input: {
              type: 'string',
              enum: [...TEMPLATE_INPUT_TYPES],
              description: 'A text input of this type. Give exactly one of input, select or checkbox.',
            },
            select: { type: 'array', minItems: 1, maxItems: INTERACTIVE_TEMPLATE_OPTIONS_MAX, items: { type: 'object', properties: { value: { type: 'string' }, label: { type: 'string' } }, required: ['value', 'label'] } },
            checkbox: { type: 'boolean', enum: [true] },
            value: { type: ['string', 'number', 'boolean'], description: 'The pre-filled value (a checkbox: true when checked)' },
            placeholder: { type: 'string' },
            required: { type: 'boolean' },
            source: { type: 'string', description: 'Where the pre-filled value came from, shown under the field' },
          },
          required: ['name', 'label'],
          oneOf: [{ required: ['input'] }, { required: ['select'] }, { required: ['checkbox'] }],
        },
      },
    },
    required: ['fields'],
  },
  views: {
    main: {
      type: 'card',
      children: [
        { type: 'title', if: 'params.title', text: '{{params.title}}' },
        {
          type: 'form',
          submit: 'submit',
          submit_label: '{{params.submit_label}}',
          children: [
            {
              type: 'list',
              each: 'params.fields',
              item: {
                type: 'col',
                children: [
                  {
                    type: 'input',
                    if: 'item.input',
                    name: '{{item.name}}',
                    label: '{{item.label}}',
                    input_type: '{{item.input}}',
                    placeholder: '{{item.placeholder}}',
                    default_value: '{{item.value}}',
                    required: '{{item.required}}',
                    source: '{{item.source}}',
                  },
                  {
                    type: 'select',
                    if: 'item.select',
                    name: '{{item.name}}',
                    label: '{{item.label}}',
                    options: '{{item.select}}',
                    placeholder: '{{item.placeholder}}',
                    default_value: '{{item.value}}',
                    required: '{{item.required}}',
                    source: '{{item.source}}',
                  },
                  {
                    type: 'checkbox',
                    if: 'item.checkbox',
                    name: '{{item.name}}',
                    label: '{{item.label}}',
                    default_checked: '{{item.value}}',
                    required: '{{item.required}}',
                    source: '{{item.source}}',
                  },
                ],
              },
            },
          ],
        },
      ],
    },
  },
  actions: {
    submit: { type: 'send', values: '{{form}}' },
  },
  fallback: '{{#if params.title}}{{params.title}}\n{{/if}}{{#each params.fields}}- {{label}}{{#if value}}: {{value}}{{/if}}\n{{/each}}',
};

export interface InteractiveBuiltinTemplate {
  version: number;
  definition: InteractiveTemplateDefinition;
}

/** Every built-in version WayAI ships, oldest first per name. */
export const INTERACTIVE_BUILTIN_TEMPLATES: readonly InteractiveBuiltinTemplate[] = [
  { version: 1, definition: choices },
  { version: 1, definition: buttons },
  { version: 1, definition: form },
];

/** The built-in a message names, at the version it pins; null when there is none. */
export function findInteractiveBuiltinTemplate(name: string, version: number): InteractiveTemplateDefinition | null {
  return INTERACTIVE_BUILTIN_TEMPLATES.find((t) => t.definition.name === name && t.version === version)?.definition ?? null;
}
